import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {TaskViewContext} from './task_view.context';
import {
  CurrentProcess,
  CurrentTask, ProcessTask,
  SuccessClaimCallbackData
} from '../../types';
import {AlertSettingsProps} from '../../../settings/alert_alarm/types';
import {ProcessActionPopupData, ProcessCode} from '../../../processes/types';
import {GroupListProps} from '../../../../constants/types';
import {
  getAvailableProcessAssigneeGroups, getCountOfProcessTasks,
  getProcessDetailsById, getTasksDetailsByProcessId
} from '../../../../api/processes';
import {getTask, getTasksAfterGiven} from '../../../../api/tasks';
import {
  getAlertSettingsByProcessType,
  getTaskAlertSettingsByProcessType
} from '../../../../api/settings/alert_alarm';
import {DEFAULT_ERROR} from '../../../../api/constants';
import {
  defaultDelay,
  defaultTasksCount, notRunningDelay
} from '../../../../constants/constants';
import {
  createArraysOfDuplicates,
  getAvailableCurrentTasks,
  getRefreshDelay,
  getUniqueElements
} from '../../helpers';
import {useAppSelector} from '../../../../store/hooks';
import {useSnackbarHook} from '../../../../hooks/useSnackbar';

type Params = {processId: string}

export const TaskViewProvider: FC = ({children}) => {
  const {processId} = useParams<Params>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [process, setProcess] = useState<Nullable<CurrentProcess>>(null);
  const [currentTasks, setCurrentTasks] =
    useState<Nullable<CurrentTask[]>>(null);
  const [taskAlertData, setTaskAlertData] =
    useState<Nullable<AlertSettingsProps>>(null);
  const [processAlertData, setProcessAlertData] =
    useState<Nullable<AlertSettingsProps>>(null);
  // TODO: move process assignee popup functionality to custom hook and reuse
  const [isActionProcessing, setIsActionProcessing] = useState(false);
  const [processActionsPopupData, setProcessActionsPopupData] =
    useState<Nullable<ProcessActionPopupData>>(null);
  const [availableProcessAssigneeGroups, setAvailableProcessAssigneeGroups] =
    useState<Nullable<GroupListProps[]>>(null);
  const [tasks, setTasks] = useState<ProcessTask[]>([]);
  const [delay, setDelay] = useState(defaultDelay);
  const [tasksCount, setTasksCount] = useState(defaultTasksCount);
  const {
    user: {groups: userGroups},
    permissions: {permissions: userPermissions}
  } = useAppSelector((state) => state);
  const {addErrorToast} = useSnackbarHook();

  const currentTasksIds = useMemo(() =>
    currentTasks?.map(({id}) => id),
  [currentTasks]
  );

  const taskIdsToRefresh = useMemo(() => {
    if (currentTasksIds?.length) return currentTasksIds;

    if (!tasks?.length) return [];

    const lastCompletedTask = tasks[tasks.length - 1];

    return [lastCompletedTask.id];
  }, [currentTasksIds, tasks]);

  const formattedTasks = useMemo(() => {
    return createArraysOfDuplicates(tasks, 'seqNum');
  }, [tasks]);

  const getData = useCallback(async () => {
    setIsLoading(true);

    try {
      const currentProcess = await getProcessDetailsById(processId);
      const {type} = currentProcess || {};
      const {code: processCode = null} = type || {};

      const processTasks = await getTasksDetailsByProcessId(`${processId}`);

      const availableRunningProcessTasks =
        getAvailableCurrentTasks(processTasks, userGroups, userPermissions);

      const runningTasksPromises = availableRunningProcessTasks?.length ?
        availableRunningProcessTasks.map(({id}) => getTask(Number(id))) : [];

      const [
        taskAlertRange,
        processSettings,
        availableProcessAssigneeGroupsResponse,
        ...tasks
      ] = await Promise.all([
        getTaskAlertSettingsByProcessType(processCode),
        getAlertSettingsByProcessType(processCode),
        getAvailableProcessAssigneeGroups(processCode),
        ...runningTasksPromises
      ]);

      if (tasks?.length) {
        setCurrentTasks(tasks);
      }

      setTasks(processTasks);
      setDelay(getRefreshDelay(processTasks));
      setProcess(currentProcess);
      setTaskAlertData(taskAlertRange);
      setProcessAlertData(processSettings);
      setAvailableProcessAssigneeGroups(availableProcessAssigneeGroupsResponse);
    } catch (e) {
      setError(e?.message || DEFAULT_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [processId, userGroups, userPermissions]);

  useEffect(() => {
    getData();
  }, [getData]);

  const successClaimCallback = useCallback(({
    assignee, taskId
  }: SuccessClaimCallbackData) => {
    if (!currentTasks?.length) return;

    const updatedTasks = currentTasks.map((task) => {
      if (taskId !== task.id) return task;

      return {...task, assignee, claimed: true};
    });

    setCurrentTasks(updatedTasks);
  }, [currentTasks]);

  const getProcessDetails = useCallback(async () => {
    try {
      const processDetailsResponse =
        await getProcessDetailsById(processId);
      setProcess(processDetailsResponse);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    }
  }, [processId]);

  const getTasksCount = useCallback(async () => {
    if (!processId) return;

    try {
      const countOfProcessTasks =
        await getCountOfProcessTasks(Number(processId));

      setTasksCount(countOfProcessTasks);
    } catch (error) {
      addErrorToast(error.message);
    }
  }, [processId]);

  useEffect(() => {
    if (!delay) return;

    const interval = setInterval(() => {
      getTasksCount();
    }, delay);

    return () => clearInterval(interval);
  }, [delay, getTasksCount]);

  const updateTasks = useCallback((newTasks = []) => {
    setTasks((prev) => getUniqueElements([...prev, ...newTasks]));
  }, []);

  const getNewTasks = useCallback(async () => {
    if (!taskIdsToRefresh?.length) return getTasksCount();

    try {
      const receivedTasks = (await Promise.all(
          taskIdsToRefresh?.map((taskId) => getTasksAfterGiven(taskId))
      )).flat(2);

      if (!receivedTasks?.length) {
        return setDelay(notRunningDelay);
      } else {
        setDelay(getRefreshDelay(receivedTasks));
      }

      const newRunningTasks =
        getAvailableCurrentTasks(receivedTasks, userGroups, userPermissions);

      if (newRunningTasks) {
        setCurrentTasks(newRunningTasks);
      }

      updateTasks(receivedTasks);
    } catch (error) {
      addErrorToast(error.message);
    }
  }, [
    taskIdsToRefresh, tasks, updateTasks,
    getTasksCount, userGroups, userPermissions
  ]);

  useEffect(() => {
    if (tasksCount && tasks && tasksCount !== tasks.length) {
      getNewTasks();
    }
  }, [tasksCount]);

  const addCompletedTaskToArray = useCallback((task) => {
    task.completedAt = task.completedAt || String(new Date());
    task.status.code = ProcessCode.COMPLETED;

    updateTasks([task]);
    getNewTasks();
  }, [tasks, updateTasks]);

  return (
    <TaskViewContext.Provider
      value={{
        processId: Number(processId),
        isLoading,
        setIsLoading,
        error,
        setError,
        process,
        currentTasks,
        setCurrentTasks,
        taskAlertData,
        processAlertData,
        isActionProcessing,
        setIsActionProcessing,
        processActionsPopupData,
        setProcessActionsPopupData,
        availableProcessAssigneeGroups,
        successClaimCallback,
        getProcessDetails,
        processStartedAt: process?.startedAt,
        formattedTasks,
        setDelay,
        addCompletedTaskToArray
      }}
    >
      {children}
    </TaskViewContext.Provider>
  );
};
